'use client';

import { REQUEST_TIMEOUT_MS } from '@/infrastructure/api/client';

import type {
  LoginCredentials,
  LoginResponse,
  SessionResponse,
  RefreshResponse,
  SwitchRoleResponse,
  PermissionsResponse,
} from './types';

export async function fetchSession(): Promise<SessionResponse> {
  const response = await fetch('/api/auth/session', {
    credentials: 'include',
  });

  if (!response.ok) {
    return { authenticated: false };
  }

  return response.json();
}

export async function loginUser(
  credentials: LoginCredentials,
): Promise<LoginResponse> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(credentials),
  });

  return response.json();
}

export async function logoutUser(): Promise<{ success: boolean }> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });

  return response.json();
}

export async function refreshSession(): Promise<RefreshResponse> {
  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    // Carry the HTTP status so callers can tell a dead session (401) from a
    // transient failure (503/network) that must not clear local auth state.
    const error = new Error(`Session refresh failed: ${response.status}`) as Error & {
      status: number;
    };
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function switchRole(
  tenantUuid: string,
  roleUuid: string,
): Promise<SwitchRoleResponse> {
  const response = await fetch('/api/auth/switch-role', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ tenantUuid, roleUuid }),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  return response.json();
}

export async function fetchPermissions(): Promise<PermissionsResponse> {
  const response = await fetch('/api/auth/permissions', {
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  if (!response.ok) {
    return { permissions: [] };
  }

  return response.json();
}
